import { Component, OnInit, ViewChildren, QueryList, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/finally';
import 'rxjs/add/observable/zip';

import { ToastService } from 'libs/mdbootstrap/typescripts/pro/index';
import { CompleterComponent } from 'libs/mdbootstrap/typescripts/pro/autocomplete';

import * as Models from 'app/core/models';
import { ProcessService } from 'app/core/process/process.service';
import { AuthenticationService } from 'app/core/authentication/authentication.service';
import { ModalService } from 'app/modal/modal.service';
import { LookupService } from 'app/core/lookup/lookup.service';
import { UserService } from 'app/core/user/user.service';
import { I18nService } from 'app/core/i18n.service';
import { PageTitleService } from '../shared/helpers/pageTitle.service';

@Component({
  selector: 'app-preview-process',
  templateUrl: './previewProcess.component.html',
  styleUrls: ['./previewProcess.component.scss']
})
export class PreviewProcessComponent implements OnInit, OnDestroy {

  @ViewChildren(CompleterComponent) completers: QueryList<CompleterComponent>;

  process: Models.Process = null;
  showContent = false;
  isLikeLoading = false;
  isLoading = true;
  private error: any = null;
  private processId: number = null;
  private showError = false;
  private stepTypes: Models.StepType[] = [];
  private currentStageIndex = 0;
  private currentStepIndex = 0;
  private processLanguage: Models.Languages = Models.Languages.enUS;

  constructor(private formBuilder: FormBuilder,
    private processService: ProcessService,
    private lookupService: LookupService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private actRoute: ActivatedRoute,
    private translateService: TranslateService,
    private modalService: ModalService,
    private toastrService: ToastService,
    private userService: UserService,
    private domSanitizer: DomSanitizer,
    private pageTitleService: PageTitleService,
    private i18nService: I18nService) {
  }

  ngOnInit() {
    this.isLikeLoading = false;
    this.isLoading = true;
    this.processId = +this.actRoute.snapshot.paramMap.get('id');
    this.processLanguage = Models.Languages.enUS;
    Observable
      .zip(
        this.processService.getPublished(this.processId),
        this.lookupService.getStepTypes(Models.Languages.enUS)
      )
      .finally(() => {
        this.isLoading = false;
      })
      .subscribe(([process, stepTypes]) => {
        this.process = process;
        this.pageTitleService.setTitle(this.actRoute.data['value'].titleKey, this.process.title);
        this.processLanguage = !process.isRTL ? Models.Languages.enUS : Models.Languages.heIL;
        this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, this.processLanguage);

        this.processData();
        this.stepTypes = stepTypes;
        this.currentStageIndex = 0;
        this.currentStepIndex = 0;
        this.error = null;
        this.showError = false;
        this.showContent = true;
      }, error => {
        this.error = error;
        this.showContent = false;
        this.showError = true;
      });
  }

  ngOnDestroy(): void {
    this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, Models.Languages.enUS);
  }

  // Share Buttons
  get shareRepoUrl(): string {
    return window.location.href;
  }

  get shareImageUrl(): string {
    return this.process ? this.process.coverSmallUrl : 'http://proactiview-client.azurewebsites.net/assets/images/logo.png';
  }

  get shareText(): string {
    return this.process ? this.process.title : 'ProactView';
  }

  get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  get errorMessage(): string {
    if (this.error) {
      const errorBody = this.error._body ? JSON.parse(this.error._body) : null;
      if (errorBody && errorBody.error) {
        return errorBody.error;
      }
    }
    // @TODO: translate
    return 'There was a problem.';
  }

  public getStepMedia(stageOrder: number, stepOrder: number): Models.Media {
    const stage: Models.ProcessStage = this.process.stages.find(x => x.order === stageOrder);
    const step: Models.ProcessStep = stage.steps.find(x => x.order === stepOrder);
    return step.medias.length ? step.medias[0] : null;
  }

  public getMediaImgae(media: Models.Media): string {
    if (!media) {
      return '';
    }
    if (media.mediaType === 1) {
      return media.smallUrl;
    } else if (media.mediaType === 2) {
      return (this.getVideoThumbnail(media));
    }
    return '';
  }

  runProcess() {
    this.router.navigate(['/process/execute/', this.processId]);
  }

  getStepTypeImage(stepTypeId: number): string {
    if (stepTypeId) {
      return this.stepTypes.find(x => x.stepTypeId === stepTypeId).imageUrl;
    } else {
      return 'https://proactiviewstaging.blob.core.windows.net/step-type-icons/default.png';
    }
  }

  getVideoId(url: string): string {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  }

  getVideoThumbnail(video: Models.Media): string {
    if (!video.mediaUrl) {
      return '';
    }
    const videoId: string = this.getVideoId(video.mediaUrl);
    if (!videoId) {
      return '';
    }
    return 'https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg';
  }

  edit() {
    if (!this.process.canEdit) {
      return;
    } else {
      this.router.navigate(['/process/editing/', this.processId]);
    }
  }

  showStepContent(stageIndex: number, stepIndex: number): boolean {
    if (stageIndex === this.currentStageIndex && stepIndex === this.currentStepIndex) {
      return true;
    }
    return false;
  }

  setCurrentStep(stageIndex: number, stepIndex: number): void {
    this.currentStageIndex = stageIndex;
    this.currentStepIndex = stepIndex;
  }

  like(): void {
    this.isLikeLoading = true;
    this.processService.likeProcess(this.processId)
      .finally(() => {
        this.isLikeLoading = false;
      })
      .subscribe(() => {
        this.error = undefined;
        this.process.hasLiked = true;
        this.process.analytics.likes++;
      }, error => {
        this.error = error;
        this.toastrService.error(this.errorMessage);
      });
  }

  unPublish(): void {
    const data: Models.ModalContext = {
      modalType: Models.ModalType.CONFIRMATION,
      modalAction: Models.ModalAction.OPEN,
      parameters: ['unpublishProcessTtile', 'unpublishProcessMsg1', 'unpublishProcessMsg2']
    };
    this.modalService.command(data);

    const subscription = this.modalService.returnEvent$.subscribe((result: boolean) => {
      subscription.unsubscribe();
      const resultData: Models.ModalContext = {
        modalType: Models.ModalType.CONFIRMATION,
        modalAction: Models.ModalAction.CLOSE,
        parameters: []
      };

      if (result) {
        // @TODO: translate
        this.processService.unPublishProcess(this.processId)
          .finally(() => { })
          .subscribe(nextResult => {
            this.modalService.command(resultData);
            this.toastrService.success('The process was unpublished successfully.', 'Sucess!');
            this.router.navigate(['/home']);
          }, error => {
            this.modalService.command(resultData);
            this.error = error;
            this.toastrService.error(this.errorMessage, 'There was a problem');
          });
      } else {
        data.modalAction = Models.ModalAction.CLOSE;
        this.modalService.command(data);
      }
    });
  }

  archive(): void {
    const data: Models.ModalContext = {
      modalType: Models.ModalType.CONFIRMATION,
      modalAction: Models.ModalAction.OPEN,
      parameters: ['archiveProcessTitle', 'archiveProcessMsg1', 'archiveProcessMsg2']
    };
    this.modalService.command(data);

    const subscription = this.modalService.returnEvent$.subscribe((result: boolean) => {
      subscription.unsubscribe();
      const resultData: Models.ModalContext = {
        modalType: Models.ModalType.CONFIRMATION,
        modalAction: Models.ModalAction.CLOSE,
        parameters: []
      };

      if (result) {
        this.processService.archiveProcess(this.processId)
          .finally(() => { })
          .subscribe(() => {
            // @TODO: translate
            this.modalService.command(resultData);
            this.toastrService.success('The process was archived successfully.', 'Sucess!');
            this.router.navigate(['/home']);
          }, error => {
            this.modalService.command(resultData);
            this.error = error;
            this.toastrService.error(this.errorMessage, 'There was a problem');
          });
      } else {
        data.modalAction = Models.ModalAction.CLOSE;
        this.modalService.command(resultData);
      }
    });
  }

  duplicate(): void {
    const data: Models.ModalContext = {
      modalType: Models.ModalType.CONFIRMATION,
      modalAction: Models.ModalAction.OPEN,
      parameters: ['duplicateProcessTitle', 'duplicateProcessMsg1', 'duplicateProcessMsg2']
    };
    this.modalService.command(data);

    const subscription = this.modalService.returnEvent$.subscribe((result: boolean) => {
      subscription.unsubscribe();
      const resultData: Models.ModalContext = {
        modalType: Models.ModalType.CONFIRMATION,
        modalAction: Models.ModalAction.CLOSE,
        parameters: []
      };

      if (result) {
        this.processService.duplicateProcess(this.processId)
          .finally(() => { })
          .subscribe((result: Models.Process) => {
            // @TODO: translate
            this.modalService.command(resultData);
            this.toastrService.success('The process was duplicated successfully.', 'Sucess!');
            this.router.navigate(['/process/editing/', result.processId]);
          }, error => {
            this.modalService.command(resultData);
            this.error = error;
            this.toastrService.error(this.errorMessage, 'There was a problem');
          });
      } else {
        data.modalAction = Models.ModalAction.CLOSE;
        this.modalService.command(resultData);
      }
    });
  }

  private processData() {
    this.process.stages.forEach(stage => {
      stage.steps.forEach(step => {
        step.medias.forEach(media => {
          if (media.mediaType === 2) {
            const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
            const match = media.mediaUrl.match(regExp);
            media.videoId = (match && match[7].length === 11) ? match[7] : null;

            if (media.videoId) {
              let url = 'https://www.youtube.com/embed/' + media.videoId;
              const endParam = media.endTime ? 'end=' + media.endTime : '';
              const startParam = media.startTime ? media.startTime : 0;
              url += '?' + endParam + '&rel=0&start=' + startParam + '&iv_load_policy=3';
              media.videoIframeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
            }
          }
        });
        if (step.tasks && step.tasks.length) {
          step.showTasks = true;
        } else {
          step.showTasks = false;
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// C:/ProactiView/ProactiView-Client/src/app/previewProcess/previewProcess.component.ts